import { Injectable } from '@angular/core';
import { first } from 'rxjs';
import { WindowService } from 'shared/services/window/window.service';
import { NewRelicEnvironmentConfig } from 'environments/models/new-relic-config.model';
import { CanonicalEnvironmentName, GenesysCloudConfigService } from 'common/genesys-cloud-config';
import { TelemetryService } from '../telemetry.service';
import { BcConfigService } from '../../config/provider';
import { NewRelicBrowserApi } from './new-relic-browser-api.model';

@Injectable()
export class NewRelicTelemetryService implements TelemetryService {
  private readonly newRelicBrowserApi?: NewRelicBrowserApi;

  constructor(private readonly configService: BcConfigService,
              private readonly windowService: WindowService,
              private readonly genesysCloudConfigService: GenesysCloudConfigService
  ) {
    this.newRelicBrowserApi = windowService.getWindow()?.newrelic;
    if (!this.newRelicBrowserApi) {
      throw new Error('Global object "newrelic" is not initialized');
    }

    this.genesysCloudConfigService.canonicalEnvironmentName$.pipe(
      first()
    ).subscribe({
      next: env => this.setGlobalConfig(env),
      error: (err: any) => {
        throw new Error(`NewRelic initialization failed with reason: ${ err }`);
      }
    });
  }

  reportError(error: Error | string): void {
    this.newRelicBrowserApi?.noticeError(error, {
      //seems, error message is missing in NewRelic (errorMessage field is always empty)
      description: (error as Error)?.message || error?.toString()
    });
  }

  private getConfig(env: CanonicalEnvironmentName): Partial<NewRelicEnvironmentConfig> {
    const nrConfig = this.configService.current.telemetry?.newRelic;
    if (!nrConfig) {
      throw new Error('No NewRelic root config found');
    }

    const envConfig = nrConfig[env];
    if (!envConfig) {
      throw new Error(`No NewRelic config found for environment "${env}"`);
    }

    return {
      ...nrConfig.defaults,
      ...envConfig
    };
  }

  setGlobalConfig(env: CanonicalEnvironmentName): void {
    const config = this.getConfig(env);
    const window = this.windowService.getWindow();

    window.NREUM = window.NREUM || {};
    window.NREUM.init = {
      distributed_tracing: {
        enabled: config.distributedTracing
      },
      privacy: {
        cookies_enabled: config.cookiesEnabled
      },
      ajax: {
        deny_list: [
          ...config.ajaxDenyList
        ]
      }
    };

    window.NREUM.loader_config = {
      accountID: config.accountID,
      trustKey: config.trustKey,
      agentID: config.agentID,
      licenseKey: config.licenseKey,
      applicationID: config.applicationID
    };
    window.NREUM.info = {
      beacon: config.beacon,
      errorBeacon: config.errorBeacon,
      licenseKey: config.licenseKey,
      applicationID: config.applicationID,
      sa: config.sa
    };
  }
}
